/**
 * Год и месяц на титульном слайде («2026» · «ноябрь»).
 *
 * Отчёт обычно сдают за месяц кампании, а ссылки в нём — публикации этого
 * месяца. Поэтому период берётся из дат самих публикаций: месяц, на который
 * пришлось больше всего постов. Пара постов с конца прошлого месяца не должна
 * перетянуть отчёт на себя. При равенстве берётся более поздний месяц —
 * отчёт сдают по его итогам. Пользователь может поправить год и месяц в форме.
 */

export const MONTHS_RU = [
  'январь',
  'февраль',
  'март',
  'апрель',
  'май',
  'июнь',
  'июль',
  'август',
  'сентябрь',
  'октябрь',
  'ноябрь',
  'декабрь',
] as const;

export type ReportPeriod = {
  year: number;
  /** 0 — январь, как в Date. */
  month: number;
};

/**
 * Graph API отдаёт время как «2026-09-15T09:17:12+0000» — пояс без двоеточия.
 * Chrome такое разбирает, Safari — нет (NaN), поэтому приводим к ISO.
 */
export function parseApiDate(value: string): number {
  return Date.parse(value.replace(/([+-]\d{2})(\d{2})$/, '$1:$2'));
}

// Сайт работает на Казахстан (UTC+5): пост в 23:30 последнего числа по Астане
// относится к этому месяцу, а не к следующему, как вышло бы по UTC.
const ALMATY_OFFSET_MS = 5 * 60 * 60 * 1000;

export function pickReportPeriod(dates: Array<string | null | undefined>, fallback: Date): ReportPeriod {
  const counts = new Map<number, number>();
  for (const value of dates) {
    if (!value) continue;
    const time = parseApiDate(value);
    if (Number.isNaN(time)) continue;
    const local = new Date(time + ALMATY_OFFSET_MS);
    const key = local.getUTCFullYear() * 12 + local.getUTCMonth();
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }

  let best: number | null = null;
  for (const [key, count] of counts) {
    if (best === null) {
      best = key;
      continue;
    }
    const bestCount = counts.get(best) ?? 0;
    if (count > bestCount || (count === bestCount && key > best)) best = key;
  }

  if (best === null) {
    const local = new Date(fallback.getTime() + ALMATY_OFFSET_MS);
    return { year: local.getUTCFullYear(), month: local.getUTCMonth() };
  }
  return { year: Math.floor(best / 12), month: best % 12 };
}
